import { format } from 'date-fns';
import { tss } from 'tss-react/mui';

import {
  ListItemAvatar,
  ListItemText,
  Typography,
  IconButton,
  Tooltip,
  Box,
} from '@mui/material';

import VideoIcon from '@mui/icons-material/Videocam';

import type { GenericEventTrainingFragment as TrainingSession } from '@/gql/graphql';
import { GroupOrServiceAvatar } from '@/components/Avatar/GroupOrServiceAvatar';
import { Or2ListItem } from '@/components/data-display/Or2ListItem';

const useStyles = tss.create(({ theme }) => {
  return {
    // remove 56px minWidth of avatar list item
    eventAvatarRoot: {
      minWidth: 0,
    },
    listItemAction: {
      top: '-12px',
      transform: 'none',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      height: '100%',
      paddingLeft: theme.spacing(1),
      borderLeft: `1px solid ${theme.palette.divider}`,
      paddingTop: theme.spacing(0.5),
      paddingBottom: theme.spacing(0.5),
    },
    timeText: { marginLeft: theme.spacing(2) },
    listItemSecondaryActionPadding: { paddingRight: '58px' },
  };
});

interface Props {
  session: TrainingSession;
  showOwner: boolean;
}

export const TrainingSessionListItem = ({ session, showOwner }: Props) => {
  const { classes } = useStyles();

  const { course, cancelled } = session;
  const location = session.sessionLocation?.title;

  const owner = course?.service;

  const hasSecondaryActions = Boolean(session.webinar?.joinUrl && !cancelled);

  return (
    <>
      <Typography
        component="span"
        className={classes.timeText}
        variant="caption"
        color="textSecondary"
      >
        {format(new Date(session?.start || ''), 'p')}
      </Typography>
      <Or2ListItem
        alignItems="flex-start"
        classes={{
          secondaryAction: hasSecondaryActions
            ? classes.listItemSecondaryActionPadding
            : undefined,
        }}
        hoverHighlight={false}
        secondaryAction={
          hasSecondaryActions && (
            <Box className={classes.listItemAction}>
              <Tooltip title="Join webinar">
                <IconButton
                  edge="end"
                  size="small"
                  aria-label="video-call"
                  href={session.webinar?.joinUrl || ''}
                  target="_blank"
                >
                  <VideoIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )
        }
      >
        {showOwner && (
          <ListItemAvatar className={classes.eventAvatarRoot}>
            <GroupOrServiceAvatar
              iconUrl={owner?.iconUrl}
              groupOrServiceName={owner?.name}
              sx={{ minWidth: 0, marginRight: 1.5 }}
            />
          </ListItemAvatar>
        )}

        <ListItemText
          disableTypography
          primary={<Typography variant="body2">{course?.title}</Typography>}
          secondary={
            <>
              {cancelled && (
                <>
                  <Typography component="span" variant="caption" color="error">
                    CANCELLED
                  </Typography>
                  <br />
                </>
              )}
              {location ? (
                <>
                  <Typography
                    component="span"
                    variant="caption"
                    color="textSecondary"
                  >
                    {session.sessionLocation?.title}
                  </Typography>
                  <br />{' '}
                </>
              ) : null}
              {showOwner && (
                <Typography
                  component="span"
                  variant="caption"
                  color="textSecondary"
                >
                  from {owner?.name}
                </Typography>
              )}
            </>
          }
        />
      </Or2ListItem>
    </>
  );
};
